// src/stores/index.ts
import { createContext, useContext } from 'react';
import AuthStore from './AuthStore';
import UserStore from './UserStore';
import CompanyStore from './CompanyStore';
import ContentStore from './ContentStore';
import ViolationStore from './ViolationStore';
import FeedStore from './FeedStore';
import UserWallStore from './UserWallStore';
import CertificateStore from './CertificateStore';
import ContentInteractionStore from './ContentInteractionStore';
import MarketplaceStore from './MarketplaceStore';
import LicenseStore from './LicenseStore';
import CreatorStore from './CreatorStore';
import OrphanContentStore from './OrphanContentStore';
import UnclaimedContentStore from './UnclaimedContentStore';
import PreLicenseStore from './PreLicenseStore';
import InvoiceStore from './InvoiceStore';
import PaymentStore from './PaymentStore';
import DisputeStore from './DisputeStore';
import NotificationStore from './NotificationStore';

export class RootStore {
    authStore: AuthStore;
    userStore: UserStore;
    companyStore: CompanyStore;
    contentStore: ContentStore;
    licenseStore: LicenseStore;
    violationStore: ViolationStore;
    feedStore: FeedStore;
    userWallStore: UserWallStore;
    certificateStore: CertificateStore;
    contentInteractionStore: ContentInteractionStore;
    marketplaceStore: MarketplaceStore;
    creatorStore: CreatorStore;
    orphanContentStore: OrphanContentStore;
    unclaimedContentStore: UnclaimedContentStore;
    preLicenseStore: PreLicenseStore;
    invoiceStore: InvoiceStore;
    paymentStore: PaymentStore;
    disputeStore: DisputeStore;
    notificationStore: NotificationStore; //TODO

    constructor() {
        this.authStore = new AuthStore(this);
        this.userStore = new UserStore(this);
        this.companyStore = new CompanyStore(this);
        this.contentStore = new ContentStore(this);
        this.licenseStore = new LicenseStore(this);
        this.violationStore = new ViolationStore(this);
        this.feedStore = new FeedStore(this);
        this.userWallStore = new UserWallStore(this);
        this.certificateStore = new CertificateStore(this);
        this.contentInteractionStore = new ContentInteractionStore(this);
        this.marketplaceStore = new MarketplaceStore(this);
        this.creatorStore = new CreatorStore(this);
        this.orphanContentStore = new OrphanContentStore(this);
        this.unclaimedContentStore = new UnclaimedContentStore(this);
        this.preLicenseStore = new PreLicenseStore(this);

        this.invoiceStore = new InvoiceStore(this);
        this.paymentStore = new PaymentStore(this);
        this.disputeStore = new DisputeStore(this);
        this.notificationStore = new NotificationStore(this);
    }
}

const StoresContext = createContext<RootStore>(new RootStore());

export const useStores = () => useContext(StoresContext);
export const useAuthStore = () => useStores().authStore;
export const useUserStore = () => useStores().userStore;
export const useCompanyStore = () => useStores().companyStore;
export const useContentStore = () => useStores().contentStore;
export const useLicenseStore = () => useStores().licenseStore;
export const useViolationStore = () => useStores().violationStore;
export const useFeedStore = () => useStores().feedStore;
export const useUserWallStore = () => useStores().userWallStore;
export const useCertificateStore = () => useStores().certificateStore;
export const useContentInteractionStore = () => useStores().contentInteractionStore;
export const useMarketplaceStore = () => useStores().marketplaceStore;
export const useCreatorStore = () => useStores().creatorStore;
export const useOrphanContentStore = () => useStores().orphanContentStore;
export const useUnclaimedContentStore = () => useStores().unclaimedContentStore;
export const usePreLicenseStore = () => useStores().preLicenseStore;
export const useInvoiceStore = () => useStores().invoiceStore;
export const usePaymentStore = () => useStores().paymentStore;
export const useDisputeStore = () => useStores().disputeStore;

// Export specific store types for type safety
export type {
    AuthStore, UserStore, CompanyStore, ContentStore, LicenseStore, ViolationStore, FeedStore,
    UserWallStore, CertificateStore, ContentInteractionStore, MarketplaceStore, CreatorStore, 
    OrphanContentStore, UnclaimedContentStore, PreLicenseStore, InvoiceStore, PaymentStore, DisputeStore
};

// Generic store hook with selector
export const useStoreSelector = <T, R>(
    storeSelector: (rootStore: RootStore) => T,
    selector: (store: T) => R
): R => {
    const rootStore = useStores();
    const store = storeSelector(rootStore);
    return selector(store);
};

// Specialized hooks for content interactions
export const useContentInteractions = (contentId?: string) => {
    const contentInteractionStore = useContentInteractionStore();
    const contentStore = useContentStore();
    
    return {
        // Like functionality
        toggleLike: (contentId: string) => contentInteractionStore.toggleLike(contentId),
        isLiked: (contentId: string) => contentInteractionStore.isLiked(contentId),
        getLikesCount: (contentId: string) => contentInteractionStore.getLikesCount(contentId),
        
        // View functionality
        trackView: (contentId: string) => contentInteractionStore.trackView(contentId),
        getViewsCount: (contentId: string) => contentInteractionStore.getViewsCount(contentId),
        
        // Download functionality
        trackDownload: (contentId: string) => contentInteractionStore.trackDownload(contentId),
        getDownloadsCount: (contentId: string) => contentInteractionStore.getDownloadsCount(contentId),
        
        // Content data
        getContentById: (contentId: string) => contentStore.getContentById(contentId),
    };
};

// Specialized hooks for unclaimed content
export const useUnclaimedContentWorkflow = () => {
    const unclaimedStore = useUnclaimedContentStore();
    const authStore = useAuthStore();
    
    return {
        // Content discovery
        discoverContent: (source?: string) => unclaimedStore.initialize(source as any),
        searchContent: (query: string) => unclaimedStore.searchContent(query),
        getContentBySource: (source: string) => unclaimedStore.contentBySource[source as any] || [],
        
        // Claims management
        submitClaim: (contentId: string, evidence: any) => unclaimedStore.submitClaim(contentId, evidence),
        trackClaimStatus: (claimId: string) => unclaimedStore.getClaimRequestById(claimId),
        hasPendingClaim: (contentId: string) => unclaimedStore.hasPendingClaim(contentId),
        
        // Admin functions
        canReviewClaims: () => {
            const user = authStore.currentUser;
            return user?.roles?.some(role => ['admin', 'staff', 'legal'].includes(role.code)) || false;
        },
        reviewClaim: (claimId: string, decision: 'approve' | 'reject', notes?: string) => 
            unclaimedStore.reviewClaimRequest(claimId, decision, notes),
        
        // Stats and insights
        getStats: () => unclaimedStore.stats,
        getTotalValue: () => unclaimedStore.totalPotentialRevenue,
        getHighValueContent: () => unclaimedStore.highValueContent,
    };
};

// Specialized hooks for pre-license workflow
export const usePreLicenseWorkflow = () => {
    const preLicenseStore = usePreLicenseStore();
    const authStore = useAuthStore();
    
    return {
        // External work search
        searchExternalWorks: (query: string, filters?: any) => 
            preLicenseStore.searchExternalWorks({ query, ...filters }),
        getSearchResults: () => preLicenseStore.searchResults,
        getRecentSearches: () => preLicenseStore.recentSearches,
        
        // Pre-license requests
        createRequest: (workData: any, licenseType: string) => 
            preLicenseStore.createPreLicenseRequest(workData, licenseType),
        getUserRequests: () => preLicenseStore.userRequests,
        getLegalTeamRequests: () => preLicenseStore.legalTeamRequests,
        
        // CMO organizations
        getCMOs: () => preLicenseStore.availableCMOs,
        loadCMOs: () => preLicenseStore.loadCMOs(),
        
        // Role-based access
        isLegalTeamMember: () => {
            const user = authStore.currentUser;
            return user?.roles?.some(role => ['legal', 'admin', 'staff'].includes(role.code)) || false;
        },
        
        // Stats
        getStats: () => preLicenseStore.dashboardStats,
    };
};

// Lifecycle hook for content viewing
export const useContentLifecycle = (contentId: string) => {
    const { trackView } = useContentInteractions();
    
    // Auto track view when component mounts
    React.useEffect(() => {
        if (contentId) {
            trackView(contentId);
        }
    }, [contentId, trackView]);
    
    return { contentId };
};

// Combined store hook for complex operations
export const useCombinedStores = () => {
    const rootStore = useStores();

    return {
        auth: rootStore.authStore,
        user: rootStore.userStore,
        company: rootStore.companyStore,
        content: rootStore.contentStore,
        license: rootStore.licenseStore,
        violation: rootStore.violationStore,
        feed: rootStore.feedStore,
        userWall: rootStore.userWallStore,
        certificate: rootStore.certificateStore,
        contentInteraction: rootStore.contentInteractionStore,
        marketplace: rootStore.marketplaceStore,
        creator: rootStore.creatorStore,
        orphanContent: rootStore.orphanContentStore,
        unclaimedContent: rootStore.unclaimedContentStore,
        preLicense: rootStore.preLicenseStore,
        invoice: rootStore.invoiceStore,
        payment: rootStore.paymentStore,
        dispute: rootStore.disputeStore,
    };
};

// Store persistence helper
export const persistStore = () => {
    const rootStore = useStores();

    // Add persistence logic here if needed
    // This could save store state to localStorage, IndexedDB, etc.

    return {
        save: () => {
            // Implementation for saving store state
            // Could save specific stores that need persistence
            console.log('Saving store state...');
        },
        restore: () => {
            // Implementation for restoring store state
            console.log('Restoring store state...');
        },
    };
};

// Store reset helper for logout
export const resetStores = () => {
    const rootStore = useStores();

    // Reset all stores except auth (auth handles its own reset)
    rootStore.userStore = new UserStore(rootStore);
    rootStore.companyStore = new CompanyStore(rootStore);
    rootStore.contentStore = new ContentStore(rootStore);
    rootStore.licenseStore = new LicenseStore(rootStore);
    rootStore.violationStore = new ViolationStore(rootStore);
    rootStore.feedStore = new FeedStore(rootStore);
    rootStore.userWallStore = new UserWallStore(rootStore);
    rootStore.certificateStore = new CertificateStore(rootStore);
    rootStore.contentInteractionStore = new ContentInteractionStore(rootStore);
    rootStore.marketplaceStore = new MarketplaceStore(rootStore);
    rootStore.creatorStore = new CreatorStore(rootStore);
    rootStore.orphanContentStore = new OrphanContentStore(rootStore);
    rootStore.unclaimedContentStore = new UnclaimedContentStore(rootStore);
    rootStore.preLicenseStore = new PreLicenseStore(rootStore);
    rootStore.invoiceStore = new InvoiceStore(rootStore);
    rootStore.paymentStore = new PaymentStore(rootStore);
    rootStore.disputeStore = new DisputeStore(rootStore);


};

// Initialize stores helper
export const initializeStores = async () => {
    const rootStore = useStores();
    
    try {
        // Initialize core stores first
        await rootStore.authStore.checkAuthStatus();
        
        // Initialize other stores if user is authenticated
        if (rootStore.authStore.isAuthenticated) {
            await Promise.all([
                rootStore.userStore.fetchCurrentProfile(),
                rootStore.companyStore.fetchCurrentCompanyProfile(),
                rootStore.feedStore.initialize(),
                rootStore.marketplaceStore.initialize(),
                rootStore.preLicenseStore.initialize(),
            ]);
        }
        
        console.log('Stores initialized successfully');
    } catch (error) {
        console.error('Failed to initialize stores:', error);
    }
};

// Export for easier testing
export const createTestStore = () => new RootStore();
